import styled from "styled-components";
import moment from "moment-timezone";
import { RAMap, RATypography, RAButtonPrimary } from "@/components/customs";
import { Row, Col, Divider, Skeleton, Grid } from "antd";
import { SvgIconMultiLeg, SvgIconOneWay, SvgIconRoundtrip, SvgPlus } from "@/assets/icons";
import { FormRequestFlightItem } from "../molecules";
import { FLIGHT_REQUEST_OPTIONS, MAX_LEG_REQUEST_CHARTER } from "@/config/constants";
import { initLegInfo } from "@/utils/initializing";
import { getLegEndDatetime, isBeforeDatetime } from "@/utils";
import { useState } from "react";

const { useBreakpoint } = Grid;

export function FormRequestFlightInfo({
	data = {},
	onChange,
	onNext,
	allowSelectAirport = true,
	headerRender,
	switchOptions = true,
}) {
	const screens = useBreakpoint();
	const { type, legs = [] } = data;
	const [error, setError] = useState([]);

	const getTrips = () => {
		let trips = [];
		legs.forEach((leg) => {
			const { from_airport, to_airport } = leg;
			if (from_airport && to_airport) {
				trips.push(leg);
			}
		});
		return trips;
	};

	const onAddNewLeg = () => {
		if (legs?.length < MAX_LEG_REQUEST_CHARTER) {
			let newLegs = [...legs];
			let newLeg = { ...initLegInfo };
			let prevLeg = legs[legs?.length - 1];
			if (prevLeg.to_airport) {
				newLeg.from_airport = prevLeg.to_airport;
			}
			newLegs.push(newLeg);
			onChange({ ...data, legs: newLegs });
		}
	};

	const onRemoveLeg = (index) => {
		let newLegs = [...legs];
		newLegs.splice(index, 1);
		onChange({ ...data, legs: newLegs });
	};

	const onSwitchOptions = () => {
		let newLegs = [...legs];
		const fromAirport = newLegs[0].to_airport;
		const toAirport = newLegs[0].from_airport;
		newLegs[0].from_airport = fromAirport;
		newLegs[0].to_airport = toAirport;

		if (type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP && newLegs.length >= 1) {
			newLegs[1].from_airport = toAirport;
			newLegs[1].to_airport = fromAirport;
		}
		onChange({
			...data,
			legs: newLegs,
		});
	};

	const onHanldeNextStep = () => {
		let valid = true;

		let errs = legs.map((item = {}, index) => {
			let values = {};
			if (!item.from_airport) {
				values.from_airport = "Please select departure airport.";
				valid = false;
			}
			if (!item.to_airport) {
				values.to_airport = "Please select arrival airport.";
				valid = false;
			}
			if (item.from_airport && item.to_airport && item.from_airport?._id === item.to_airport?._id) {
				values.from_airport = "Departure and arrival airports should be different";
				valid = false;
			}
			if (!item.passengers) {
				values.passengers = "Please enter passengers.";
				valid = false;
			}
			if (!item?.departureWindow && !item.departure_datetime) {
				values.departure_datetime = "Please enter departure date.";
				valid = false;
			}
			if (item?.departureWindow && (!item?.departure_datetime || !item?.departure_window_datetime)) {
				values.departure_window_datetime = "Please enter departure window.";
				valid = false;
			}
			if (item?.departureWindow && isBeforeDatetime(item?.departure_window_datetime, item?.departure_datetime)) {
				values.departure_window_datetime = "Departure window end must be after its start.";
				valid = false;
			}
			// each leg must depart after the previous leg is over
			if (index > 0 && isBeforeDatetime(item?.departure_datetime, getLegEndDatetime(legs[index - 1]))) {
				let message =
					type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP
						? "Return date must be after the departure date."
						: `Leg ${index + 1} date must be after leg ${index} date.`;
				if (item?.departureWindow) {
					values.departure_window_datetime = message;
				} else {
					values.departure_datetime = message;
				}
				valid = false;
			}
			return values;
		});

		if (!valid) {
			setError(errs);
		} else {
			onNext();
		}
	};

	const _renderHeader = () => {
		if (headerRender) return headerRender;
		return (
			<Row>
				<Col md={24}>
					<WrapperContent>
						<RATypography variant={screens.xs ? "h4" : "h3"}>Request A Flight</RATypography>
					</WrapperContent>
					<RATypography variant="body3" top="12px" color="#595959">
						Please enter your flight request details below and our team will contact you shortly!
					</RATypography>
				</Col>
			</Row>
		);
	};

	const _renderFlightOptions = () => {
		if (!allowSelectAirport) return null;
		return (
			<WrapperFlightOptions>
				<Row
					gutter={[
						{ md: 32, sm: 16, xs: 16 },
						{ md: 16, sm: 12, xs: 10 },
					]}
					align="middle"
					style={{
						width: "100%",
						paddingLeft: "6px",
					}}
				>
					<Col>
						<ButtonRequestOption
							title="One way"
							active={type === FLIGHT_REQUEST_OPTIONS.ONE_WAY}
							onChange={() => {
								if (type !== FLIGHT_REQUEST_OPTIONS.ONE_WAY) {
									onChange({
										...data,
										type: FLIGHT_REQUEST_OPTIONS.ONE_WAY,
										legs: legs?.length > 0 ? [legs[0]] : [{ ...initLegInfo }],
									});
									setError([]);
								}
							}}
							icon={
								<SvgIconOneWay color={type === FLIGHT_REQUEST_OPTIONS.ONE_WAY ? "#FFF" : "#595959"} />
							}
							data-testid="legType_oneway"
						/>
					</Col>
					<Col>
						<ButtonRequestOption
							title="Round trip"
							active={type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP}
							onChange={() => {
								if (type !== FLIGHT_REQUEST_OPTIONS.ROUND_TRIP) {
									let newLegs = [];
									newLegs.push({ ...initLegInfo, _id: 0 });
									newLegs.push({ ...initLegInfo, _id: 1 });
									onChange({
										...data,
										type: FLIGHT_REQUEST_OPTIONS.ROUND_TRIP,
										legs:
											legs?.length > 0
												? [
														legs[0],
														{
															from_airport: legs[0].to_airport,
															to_airport: legs[0].from_airport,
															departure_datetime: "",
															departure_window_datetime: "",
															passengers: 1,
															_id: 1,
														},
													]
												: newLegs,
									});
									setError([]);
								}
							}}
							icon={
								<SvgIconRoundtrip
									color={type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP ? "#FFF" : "#595959"}
								/>
							}
							data-testid="legType_roundtrip"
						/>
					</Col>
					<Col>
						<ButtonRequestOption
							title="Multi-leg"
							active={type === FLIGHT_REQUEST_OPTIONS.MULTI_LEG}
							onChange={() => {
								if (type !== FLIGHT_REQUEST_OPTIONS.MULTI_LEG) {
									let newLegs = [];
									newLegs.push({ ...initLegInfo, _id: 0 });
									newLegs.push({ ...initLegInfo, _id: 1 });
									onChange({
										...data,
										type: FLIGHT_REQUEST_OPTIONS.MULTI_LEG,
										legs:
											legs?.length > 0
												? [
														legs[0],
														{
															from_airport: legs[0].to_airport,
															to_airport: "",
															departure_datetime: "",
															departure_window_datetime: "",
															passengers: 1,
															_id: 1,
														},
													]
												: newLegs,
									});
									setError([]);
								}
							}}
							icon={
								<SvgIconMultiLeg
									color={type === FLIGHT_REQUEST_OPTIONS.MULTI_LEG ? "#FFF" : "#595959"}
								/>
							}
							data-testid="legType_multileg"
						/>
					</Col>
				</Row>
			</WrapperFlightOptions>
		);
	};

	const _renderFlightInfoByType = () => {
		if (type === FLIGHT_REQUEST_OPTIONS.ONE_WAY) {
			return (
				<FormRequestFlightItem
					type={type}
					key="flight-request-item-one-way"
					switchOptions={switchOptions}
					onSwitchOptions={onSwitchOptions}
					legs={legs}
					legIndex={0}
					formValues={legs[0]}
					allowSelectAirport={allowSelectAirport}
					onChange={(values) => {
						let newLegs = [...legs];
						newLegs = newLegs.map((item, index) => {
							if (index === 0) {
								return values;
							} else {
								return item;
							}
						});
						onChange({ ...data, legs: [...newLegs] });
					}}
					error={error?.length > 0 ? error[0] : {}}
				/>
			);
		}
		if (type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP) {
			return (
				<>
					<FormRequestFlightItem
						type={type}
						key={`flight-request-item-roundtrip-0`}
						title="Departure"
						switchOptions={switchOptions}
						onSwitchOptions={onSwitchOptions}
						legs={legs}
						legIndex={0}
						formValues={legs[0]}
						allowSelectAirport={allowSelectAirport}
						onChange={(values, nextLeg) => {
							let newLegs = [...legs];
							newLegs = newLegs.map((item, index) => {
								if (index === 0) {
									return values;
								} else if (index === 1 && nextLeg) {
									return nextLeg;
								} else {
									return item;
								}
							});
							onChange({ ...data, legs: [...newLegs] });
						}}
						error={error?.length > 0 ? error[0] : {}}
					/>
					<Divider />
					<FormRequestFlightItem
						type={type}
						key={`flight-request-item-roundtrip-1`}
						title="Return"
						legs={legs}
						legIndex={1}
						formValues={legs[1]}
						prevLegDate={legs[0]?.departure_datetime}
						allowSelectAirport={allowSelectAirport}
						disabledFromDate={legs[0]?.departure_datetime || moment()}
						onChange={(values) => {
							let newLegs = [...legs];
							newLegs = newLegs.map((item, index) => {
								if (index === 1) {
									return values;
								} else {
									return item;
								}
							});
							onChange({ ...data, legs: [...newLegs] });
						}}
						error={error?.length > 1 ? error[1] : {}}
					/>
				</>
			);
		}
		if (type === FLIGHT_REQUEST_OPTIONS.MULTI_LEG) {
			return (
				<>
					{legs.map((item, index) => (
						<div key={`flight-request-item-${index}`}>
							<FormRequestFlightItem
								type={type}
								title={`Leg ${index + 1}`}
								legs={legs}
								legIndex={index}
								formValues={item}
								prevLegDate={legs[index - 1]?.departure_datetime}
								onRemoveLeg={() => onRemoveLeg(index)}
								allowRemove={allowSelectAirport && legs?.length > 1 && index + 1 === legs?.length}
								allowSelectAirport={allowSelectAirport}
								disabledFromDate={
									index > 0 ? legs[index - 1]?.departure_datetime || moment() : moment()
								}
								onChange={(values, nextLeg) => {
									let newLegs = [...legs];
									newLegs = newLegs.map((item, i) => {
										if (i === index) {
											return values;
										} else if (nextLeg && i === index + 1) {
											return nextLeg;
										} else {
											return item;
										}
									});
									onChange({ ...data, legs: [...newLegs] });
								}}
								error={error?.length >= legs?.length ? error[index] : {}}
							/>
							{legs.length === index + 1 ? null : <Divider />}
						</div>
					))}
				</>
			);
		}
		return <Skeleton style={{ margin: "12px 0" }} />;
	};

	const _renderAddMoreLeg = () => {
		if (type === FLIGHT_REQUEST_OPTIONS.MULTI_LEG && legs?.length < MAX_LEG_REQUEST_CHARTER) {
			return (
				<Row>
					<WrapperAddLeg onClick={() => onAddNewLeg()}>
						<SvgPlus />
						<RATypography variant="button2" color="#595959">
							ADD LEG
						</RATypography>
					</WrapperAddLeg>
				</Row>
			);
		}
	};

	return (
		<ContainerRequestFlight>
			{_renderHeader()}
			<Divider />
			{_renderFlightOptions()}
			{_renderFlightInfoByType()}
			{_renderAddMoreLeg()}
			<RAMap trip={getTrips()} />
			<Row justify="center" align="middle" style={{ marginTop: "40px" }}>
				<RAButtonPrimary
					title="NEXT"
					onClick={() => onHanldeNextStep()}
					width="160px"
					data-testid="flightInfo_nextBtn"
				/>
			</Row>
		</ContainerRequestFlight>
	);
}

const ButtonRequestOption = ({ active, onChange, icon, title = "", "data-testid": dataTestId = null }) => (
	<ButtonOption
		align="middle"
		justify="center"
		onClick={onChange}
		active={active?.toString()}
		gutter={{ xs: 8, sm: 10, md: 12 }}
		data-testid={dataTestId}
	>
		<Col>
			<Row align="middle">{icon}</Row>
		</Col>
		<Col>
			<RATypography variant="body3" color={active ? "#fff" : "#595959"} style={{ whiteSpace: "nowrap" }}>
				{title}
			</RATypography>
		</Col>
	</ButtonOption>
);

const ContainerRequestFlight = styled.div`
	width: "100%";
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const WrapperFlightOptions = styled.div`
	width: 100%;
	flex-wrap: nowrap;
	overflow: auto;
`;
const ButtonOption = styled(Row)`
	padding: 9px 20px;
	flex-wrap: nowrap;
	background: #f3f2f2;
	border-radius: 24px;
	cursor: pointer;
	${({ active }) =>
		active === "true" &&
		`
        background: #222222;
    `}
`;
const WrapperAddLeg = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: 16px;
	cursor: pointer;
	border-radius: 4px;
	&:hover {
		background-color: #eee;
	}
`;
